import { formatDateLocalized } from '../../config/appConfig';
import useSWR from 'swr';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { UserSessionsSnapshot } from './types';

export function userSessionsDayLabel(dayOffset: number): string {
  if (dayOffset === 0) return 'Today';
  if (dayOffset === 1) return 'Yesterday';
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dayOffset);
  return formatDateLocalized(d, { weekday: 'short', day: 'numeric', month: 'short' });
}

export type UserSessionsDayMeta = {
  offset: number;
  weekdayShort: string;
  dayNumber: number;
  monthShort: string;
  fullLabel: string;
  stripPrimary: string;
  stripSecondary: string;
  isToday: boolean;
  isYesterday: boolean;
};

export function getUserSessionsDayMeta(dayOffset: number): UserSessionsDayMeta {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dayOffset);

  const weekdayShort = formatDateLocalized(d, { weekday: 'short' });
  const dayNumber = d.getDate();
  const monthShort = formatDateLocalized(d, { month: 'short' });
  const isToday = dayOffset === 0;
  const isYesterday = dayOffset === 1;
  const fullLabel = userSessionsDayLabel(dayOffset);

  const stripPrimary = isToday ? 'Today' : isYesterday ? 'Yday' : weekdayShort;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sameMonthAsToday =
    d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  const stripSecondary =
    isToday || isYesterday || !sameMonthAsToday
      ? `${monthShort} ${dayNumber}`
      : String(dayNumber);

  return {
    offset: dayOffset,
    weekdayShort,
    dayNumber,
    monthShort,
    fullLabel,
    stripPrimary,
    stripSecondary,
    isToday,
    isYesterday,
  };
}

export const USER_SESSION_DAY_OFFSETS = [0, 1, 2, 3, 4, 5, 6] as const;

export const USER_SESSION_DAYS = USER_SESSION_DAY_OFFSETS.map(getUserSessionsDayMeta);

export function useUserSessions(dayOffset: number, initialData?: UserSessionsSnapshot) {
  const { can, permissionsLoaded } = useAuth();
  const enabled = permissionsLoaded && can('system_health', 'view');

  return useSWR<UserSessionsSnapshot>(
    enabled ? `/system-health/user-sessions?dayOffset=${dayOffset}` : null,
    async (url: string) => {
      const res = await api.get(url);
      return res.data;
    },
    {
      fallbackData: dayOffset === 0 ? initialData : undefined,
      revalidateOnFocus: dayOffset === 0,
      dedupingInterval: 10_000,
    },
  );
}
